import React, { useCallback, useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { Roles, User } from "../../../../../../../../models/user";
import UserService from "../../../../../../../../services/user.service";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Status } from "../../../../../../../../util/enums";
import { Input } from "../../../../../../../../components/Form";
import Button, { Color } from "../../../../../../../../components/Form/Button";
import Checkbox from "../../../../../../../../components/Form/Checkbox";
import Modal from "../../../../../../../../components/Generic/Modal";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import useStyles from "./styles";

interface Props {
  selectedRole?: Roles;
  isOpen: boolean;
  isLoadingUpdate: boolean;
  closeModal: () => void;
  updateRole: (roleId: number | null, userId: number, user: User) => void;
  refetchRoleList: () => void;
  refetchRolesListCount: () => void;
}

export const AssignUsersToRoleModal = ({
  selectedRole,
  isLoadingUpdate,
  isOpen,
  closeModal,
  updateRole,
  refetchRoleList,
  refetchRolesListCount,
}: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [usersList, setUsersList] = useState<User[]>();
  const [inputValue, setInputValue] = useState<string>("");
  const [filterUser, setFilterUser] = useState<User[]>();
  const [usersSelected, setUsersSelected] = useState<User[]>();
  const [usersRevoked, setUsersRevoked] = useState<User[]>();

  const [hasRevokeButtonBeClicked, setHasRevokeButtonBeClicked] = useState<boolean>(false);
  const [hasAssignButtonBeClicked, setHasAssignButtonBeClicked] = useState<boolean>(false);

  const { isFetching } = useQuery(
    ["users.active.list"],
    () =>
      UserService.getAll({
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            include: [{ association: "role" }],
            where: { companyId: currentUser?.companyId, status: Status.ACTIVE },
          },
        ]),
        attributes: JSON.stringify(["name", "lastName", "email", "id"]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          const users = response.data;

          response.data.forEach((user, index: number) => {
            const userCompany = user?.usersCompanies?.[0];

            if (userCompany) {
              delete userCompany?.id;
              delete userCompany?.companyId;

              users[index] = { ...user, ...userCompany };
            }
          });
          setUsersList(users);
          setFilterUser(users);
        }
      },
    }
  );

  const setFilterUsersBySearch = () => {
    const filterData = usersList
      ?.map(
        (user) =>
          (user?.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
            user?.lastName?.toLowerCase().includes(inputValue.toLowerCase()) ||
            user?.email?.toLowerCase().includes(inputValue.toLowerCase())) &&
          user
      )
      .filter((item) => typeof item !== "boolean");

    if (!filterData?.includes(false)) setFilterUser(filterData as User[]);
  };

  const sortFilterUsersList = useCallback(() => {
    if (usersSelected && usersSelected.length > 0) {
      const usersSelectedId = usersSelected?.map((user) => user?.id);
      const sortUsersBySelected: User[] = [];

      usersList?.forEach((user) => {
        if (usersSelectedId && usersSelectedId?.includes(user.id))
          sortUsersBySelected.unshift(user);
        else sortUsersBySelected.push(user);
      });

      setFilterUser([...sortUsersBySelected]);
    }
  }, [usersSelected]);

  const setUpdateUsersWithRole = () => {
    if (hasRevokeButtonBeClicked) {
      usersRevoked?.forEach((user) => {
        updateRole(null, user?.id, user);
      });
    }
    usersSelected?.forEach((user) => {
      if (user && selectedRole && selectedRole.id) {
        if (user.roleId !== selectedRole.id) updateRole(selectedRole?.id, user.id, user);
      }
    });
  };

  const hasUserBeingSelected = (userId: number): boolean => {
    const foundUser = usersSelected?.find((user) => user.id === userId);

    if (foundUser) return true;

    return false;
  };

  const handleAssignUser = (user: User) => {
    setUsersRevoked((state) => (state ? [...state, user] : [user]));
    const userSelectedArray = usersSelected && [...usersSelected];
    const userSelectedIndex = usersSelected?.findIndex(
      (userSelected) => userSelected.id === user.id
    );

    if (userSelectedIndex !== undefined) userSelectedArray?.splice(userSelectedIndex, 1);

    if (userSelectedArray) setUsersSelected([...userSelectedArray]);
    setHasRevokeButtonBeClicked(true);
  };

  const disabledSaveButton = (): boolean => {
    if (!usersSelected) return true;
    if (usersSelected?.length === 0) {
      if (hasRevokeButtonBeClicked) return false;

      return true;
    }

    return false;
  };

  useEffect(() => {
    if (selectedRole && usersList && usersList?.length > 0) {
      const users = usersList?.filter((user) => user.roleId === selectedRole.id);

      if (users) setUsersSelected(users);
    }
  }, [usersList, selectedRole]);

  useEffect(() => {
    if (usersSelected && usersSelected?.length > 0) sortFilterUsersList();
  }, [usersSelected]);

  useEffect(() => {
    if (!isLoadingUpdate) {
      if (hasAssignButtonBeClicked) {
        refetchRoleList();
        refetchRolesListCount();
        closeModal();
      }
    }
  }, [isLoadingUpdate]);

  return (
    <Modal
      className={classes.paper}
      open={isOpen}
      handleClose={closeModal}
      title={translate("assignUsers")}
      data-cy="assignUserModal"
      showActionButtons
      actionButtonProps={{
        textSave: hasRevokeButtonBeClicked ? translate("revoke") : translate("assign"),
        dataCySave: "saveButton",
        loading: isLoadingUpdate,
        saveDisabled: disabledSaveButton(),
        onSave: () => {
          setHasAssignButtonBeClicked(true);
          setUpdateUsersWithRole();
        },
      }}
    >
      <section className={classes.modalSection}>
        <Grid className="mb-3" container direction="row" alignItems="center" spacing={1}>
          <Grid item md={10} lg={10}>
            <Input
              name="autosuggest"
              value={inputValue}
              placeholder={translate("searchForUser")}
              type="text"
              rightIcon={<Search />}
              onChange={(event) => {
                setInputValue(event.target.value);
                if (event.target.value === "" && usersSelected && usersSelected?.length > 0)
                  sortFilterUsersList();
                else if (event.target.value === "") setFilterUser(usersList);
              }}
              onKeyDown={(keyPress) =>
                keyPress.key === "Enter" && inputValue.length > 0 && setFilterUsersBySearch()
              }
            />
          </Grid>
          <Grid item container md={2} lg={2} justifyContent="flex-end">
            <Button
              customColor={Color.PRIMARY}
              className={classes.searchButton}
              onClick={() => setFilterUsersBySearch()}
              disabled={inputValue === ""}
            >
              {translate("search")}
            </Button>
          </Grid>
        </Grid>
        <section>
          <p className={classes.modalBodyTitle}>Users</p>
          <SkeletonComponent isLoading={isFetching}>
            <div data-cy="usersListToAssign" className={classes.modalBodyList}>
              {filterUser?.length === 0 && (
                <span className={classes.notFoundMessage}>{translate("userNotFound")}</span>
              )}
              {filterUser?.map((user) => (
                <Grid
                  key={user?.id}
                  container
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  className="mt-1"
                >
                  <Checkbox
                    control={{
                      "data-cy": user.name,
                    }}
                    label={`${user.name} ${user.lastName ? user.lastName : ""}`}
                    checked={hasUserBeingSelected(user.id)}
                    disabled={hasUserBeingSelected(user.id)}
                    onChange={() => setUsersSelected((state) => state && [...state, user])}
                  />

                  {hasUserBeingSelected(user.id) && (
                    <Button
                      data-cy={`revoke-${user.name}`}
                      className={classes.modalRevokeButton}
                      customColor={Color.ERROR}
                      onClick={() => handleAssignUser(user)}
                    >
                      {translate("revoke")}
                    </Button>
                  )}
                </Grid>
              ))}
            </div>
          </SkeletonComponent>
        </section>
      </section>
    </Modal>
  );
};
